<template>
  <div class="overview-tab">
    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <div class="loading-spinner"></div>
      <p>Carregando estatísticas...</p>
    </div>

    <!-- Content -->
    <div v-else-if="stats" class="overview-content">
      <!-- Estatísticas Principais com Animação -->
      <div class="stats-grid">
        <div
          v-for="(stat, index) in statsCards"
          :key="stat.key"
          class="stat-card"
          :style="{ animationDelay: `${index * 0.1}s` }"
        >
          <div class="stat-visual">
            <div class="stat-icon-wrapper" :class="`stat-${stat.key}`">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                class="stat-svg"
              >
                <path
                  v-if="stat.key === 'visits'"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                />
                <path
                  v-else-if="stat.key === 'users'"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                />
                <path
                  v-else-if="stat.key === 'posts'"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
                <path
                  v-else-if="stat.key === 'likes'"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                />
                <path
                  v-else-if="stat.key === 'comments'"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                />
              </svg>
            </div>
            <div class="stat-trend" v-if="stat.trend">
              <span class="trend-badge" :class="getTrendClass(stat.trend)">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  class="trend-icon"
                >
                  <path
                    fill-rule="evenodd"
                    d="M12 7a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0V8.414l-4.293 4.293a1 1 0 01-1.414 0L8 10.414l-4.293 4.293a1 1 0 01-1.414-1.414l5-5a1 1 0 011.414 0L11 10.586 14.586 7H12z"
                    clip-rule="evenodd"
                  />
                </svg>
                {{ stat.trend }}
              </span>
            </div>
          </div>
          <div class="stat-info">
            <div class="stat-value">{{ formatNumber(stat.value) }}</div>
            <div class="stat-label">{{ stat.label }}</div>
          </div>
          <div class="stat-progress">
            <div
              class="progress-bar"
              :style="{ width: getProgressWidth(stat.value, stat.key) }"
            ></div>
          </div>
        </div>
      </div>

      <!-- Gráficos Modernos -->
      <div class="charts-section">
        <!-- Gráfico de Acessos -->
        <div class="chart-card">
          <div class="chart-header">
            <div>
              <h3 class="chart-title">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  class="title-icon"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                  />
                </svg>
                Acessos nos Últimos 7 Dias
              </h3>
              <p class="chart-subtitle">Visitas diárias ao blog</p>
            </div>
            <div v-if="analyticsData?.overview" class="quick-stats">
              <div class="quick-stat">
                <span class="quick-label">Hoje</span>
                <span class="quick-value">{{
                  analyticsData.overview.today
                }}</span>
              </div>
              <div class="quick-stat">
                <span class="quick-label">Visitantes</span>
                <span class="quick-value">{{
                  analyticsData.overview.todayVisitors || 0
                }}</span>
              </div>
              <div class="quick-stat">
                <span class="quick-label">Semana</span>
                <span class="quick-value">{{
                  analyticsData.overview.week
                }}</span>
              </div>
            </div>
          </div>
          <div class="chart-body">
            <canvas ref="accessChartCanvas" class="line-chart"></canvas>
          </div>
        </div>

        <!-- Posts por Tema -->
        <div class="chart-card">
          <div class="chart-header">
            <div>
              <h3 class="chart-title">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  class="title-icon"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                  />
                </svg>
                Posts por Tema
              </h3>
              <p class="chart-subtitle">Últimos 30 dias</p>
            </div>
          </div>
          <div class="chart-body">
            <div
              v-if="stats.postsByTheme && stats.postsByTheme.length > 0"
              class="themes-grid"
            >
              <div
                v-for="(theme, index) in stats.postsByTheme.slice(0, 6)"
                :key="theme.theme"
                class="theme-card"
                :style="{ animationDelay: `${index * 0.05}s` }"
              >
                <div class="theme-header">
                  <span class="theme-icon">{{
                    getThemeEmoji(theme.theme)
                  }}</span>
                  <span class="theme-name">{{ formatTheme(theme.theme) }}</span>
                </div>
                <div class="theme-count">{{ theme.count }}</div>
                <div class="theme-bar-container">
                  <div
                    class="theme-bar-fill"
                    :style="{ width: getThemeBarWidth(theme.count) }"
                  ></div>
                </div>
              </div>
            </div>
            <div v-else class="no-data">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                />
              </svg>
              <p>Nenhum tema encontrado</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Atividades em Grade -->
      <div class="activity-grid">
        <!-- Top Posts -->
        <div class="activity-card">
          <div class="activity-header">
            <h3 class="activity-title">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                class="title-icon"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
                />
              </svg>
              Posts Populares
            </h3>
            <span class="activity-badge">Top 5</span>
          </div>
          <div
            v-if="stats.popularPosts && stats.popularPosts.length > 0"
            class="activity-list"
          >
            <div
              v-for="(post, index) in stats.popularPosts.slice(0, 5)"
              :key="post.id"
              class="activity-item"
            >
              <div class="activity-rank">{{ index + 1 }}</div>
              <div class="activity-content">
                <h4 class="activity-name">
                  {{ truncateText(post.title, 40) }}
                </h4>
                <p class="activity-meta">
                  <span class="meta-item">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        d="M2 10.5a1.5 1.5 0 113 0v6a1.5 1.5 0 01-3 0v-6zM6 10.333v5.43a2 2 0 001.106 1.79l.05.025A4 4 0 008.943 18h5.416a2 2 0 001.962-1.608l1.2-6A2 2 0 0015.56 8H12V4a2 2 0 00-2-2 1 1 0 00-1 1v.667a4 4 0 01-.8 2.4L6.8 7.933a4 4 0 00-.8 2.4z"
                      />
                    </svg>
                    {{ post.likesCount }}
                  </span>
                  <span class="meta-divider">•</span>
                  <span class="meta-item">{{ post.theme }}</span>
                </p>
              </div>
            </div>
          </div>
          <div v-else class="no-data-small">
            <p>Nenhum post encontrado</p>
          </div>
        </div>

        <!-- Top Autores -->
        <div class="activity-card">
          <div class="activity-header">
            <h3 class="activity-title">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                class="title-icon"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
              Top Autores
            </h3>
            <span class="activity-badge">Top 5</span>
          </div>
          <div
            v-if="stats.topUsers && stats.topUsers.length > 0"
            class="activity-list"
          >
            <div
              v-for="(user, index) in stats.topUsers.slice(0, 5)"
              :key="user.id"
              class="activity-item"
            >
              <div class="activity-rank">{{ index + 1 }}</div>
              <UserAvatar :user="user" size="md" />
              <div class="activity-content">
                <h4 class="activity-name">{{ user.username }}</h4>
                <p class="activity-meta">
                  <span class="meta-item">{{ user.postsCount }} posts</span>
                </p>
              </div>
            </div>
          </div>
          <div v-else class="no-data-small">
            <p>Nenhum usuário encontrado</p>
          </div>
        </div>

        <!-- Status do Sistema + Páginas Populares -->
        <div class="activity-card">
          <div class="activity-header">
            <h3 class="activity-title">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                class="title-icon"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
              Páginas Mais Visitadas
            </h3>
          </div>
          <div
            v-if="analyticsData?.topPages && analyticsData.topPages.length > 0"
            class="activity-list"
          >
            <div
              v-for="(page, index) in analyticsData.topPages.slice(0, 5)"
              :key="page.path"
              class="activity-item"
            >
              <div class="activity-rank">{{ index + 1 }}</div>
              <div class="activity-content">
                <h4 class="activity-name">{{ page.path }}</h4>
                <p class="activity-meta">
                  <span class="meta-item">{{ page.views }} visitas</span>
                </p>
              </div>
            </div>
          </div>
          <div v-else class="no-data-small">
            <p>Ainda sem dados de visitas. Navegue pelo blog para gerar dados.</p>
          </div>

          <!-- System Status -->
          <div class="system-status" style="margin-top: 20px; padding-top: 20px; border-top: 1px solid #e5e7eb;">
            <div class="status-item" :class="serverOnline ? 'status-online' : 'status-offline'">
              <div class="status-indicator">
                <span class="status-dot" :style="{ background: serverOnline ? '#10b981' : '#ef4444' }"></span>
                <span v-if="serverOnline" class="status-pulse"></span>
              </div>
              <div class="status-info">
                <p class="status-label">Servidor</p>
                <p class="status-value">{{ serverOnline ? 'Online' : 'Offline' }}</p>
              </div>
            </div>
            <button
              @click="refreshStats"
              class="refresh-btn-full"
              :disabled="refreshing"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                :class="{ spinning: refreshing }"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              <span>{{
                refreshing ? "Atualizando..." : "Atualizar Dados"
              }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="!loading" class="error-state">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        class="error-icon"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
        />
      </svg>
      <h3>Erro ao carregar estatísticas</h3>
      <p>Não foi possível carregar os dados do dashboard</p>
      <button @click="retryLoad" class="retry-btn">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
          />
        </svg>
        <span>Tentar Novamente</span>
      </button>
    </div>
  </div>
</template>

<script>
import apiService from "@/services/api";
import UserAvatar from "@/components/UserAvatar.vue";

export default {
  name: "OverviewTab",
  components: {
    UserAvatar,
  },
  props: {
    stats: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      analyticsData: null,
      refreshing: false,
      serverOnline: true,
    };
  },
  computed: {
    statsCards() {
      if (!this.stats) return [];

      return [
        {
          key: "visits",
          label: "Visitas Hoje",
          value: this.analyticsData?.overview?.today || 0,
          trend: "",
          icon: "👁️",
          iconClass: "visits",
        },
        {
          key: "users",
          label: "Usuários Totais",
          value: this.stats.overview?.totalUsers || 0,
          trend: "",
          icon: "👥",
          iconClass: "users",
        },
        {
          key: "posts",
          label: "Posts Publicados",
          value: this.stats.overview?.totalPosts || 0,
          trend: "",
          icon: "📝",
          iconClass: "posts",
        },
        {
          key: "likes",
          label: "Curtidas Totais",
          value: this.stats.overview?.totalLikes || 0,
          trend: "",
          icon: "❤️",
          iconClass: "likes",
        },
        {
          key: "comments",
          label: "Comentários",
          value: this.stats.overview?.totalComments || 0,
          trend: "",
          icon: "💬",
          iconClass: "comments",
        },
      ];
    },
  },
  mounted() {
    this.loadAnalyticsData();
    this.checkServerHealth();
  },
  methods: {
    getTrendText() {
      return "";
    },

    getTrendClass(trend) {
      if (!trend) return "";
      return trend.includes("-") ? "trend-negative" : "";
    },

    getThemeEmoji(theme) {
      const emojis = {
        tecnologia: "💻",
        negócios: "💼",
        saúde: "⚕️",
        educação: "📚",
        entretenimento: "🎭",
        viagem: "✈️",
        default: "📝",
      };
      return emojis[theme?.toLowerCase()] || emojis.default;
    },

    getProgressWidth(value) {
      // Calculate progress bar width dynamically
      const stats = this.stats?.overview || {};
      const visits = this.analyticsData?.overview?.today || 0;
      const maxValue = Math.max(
        visits,
        stats.totalUsers || 0,
        stats.totalPosts || 0,
        stats.totalLikes || 0,
        stats.totalComments || 0,
        1
      ) * 1.2;
      const percentage = Math.min((value / maxValue) * 100, 100);
      return `${percentage}%`;
    },

    async loadAnalyticsData() {
      if (this.refreshing) return;

      try {
        const response = await apiService.get(
          "/api/handlers", {
            params: { module: "admin", action: "analytics-access", days: 7 },
          }
        );
        this.analyticsData = response.data || response;
        this.$nextTick(() => {
          this.updateChart();
        });
      } catch (error) {
        console.error("Erro ao carregar analytics:", error);
      }
    },

    async checkServerHealth() {
      try {
        const response = await apiService.get("/api/health");
        this.serverOnline = !!(response && (response.success === true || response.status === 'OK'));
      } catch (e) {
        this.serverOnline = false;
      }
    },

    updateChart() {
      const canvas = this.$refs.accessChartCanvas;
      if (!canvas || !this.analyticsData?.daily) return;

      const ctx = canvas.getContext("2d");
      const data = this.analyticsData.daily;

      this.drawChart(ctx, data);
    },

    drawChart(ctx, data) {
      const canvas = ctx.canvas;
      canvas.width = canvas.parentElement.clientWidth;
      canvas.height = 280;

      if (!data || data.length === 0) return;

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const maxValue = Math.max(...data.map((d) => d.total), 1);
      const padding = { top: 30, right: 30, bottom: 50, left: 50 };
      const chartWidth = canvas.width - padding.left - padding.right;
      const chartHeight = canvas.height - padding.top - padding.bottom;
      const pointWidth = chartWidth / (data.length - 1 || 1);

      // Solid background fill
      ctx.fillStyle = "rgba(59, 130, 246, 0.05)";
      ctx.fillRect(padding.left, padding.top, chartWidth, chartHeight);

      // Draw grid lines
      ctx.strokeStyle = "#e5e7eb";
      ctx.lineWidth = 1;
      for (let i = 0; i <= 5; i++) {
        const y = padding.top + (chartHeight / 5) * i;
        ctx.beginPath();
        ctx.moveTo(padding.left, y);
        ctx.lineTo(canvas.width - padding.right, y);
        ctx.stroke();

        // Y-axis labels
        const value = Math.round(maxValue - (maxValue / 5) * i);
        ctx.fillStyle = "#6b7280";
        ctx.font = "11px system-ui, -apple-system, sans-serif";
        ctx.textAlign = "right";
        ctx.fillText(value.toString(), padding.left - 10, y + 4);
      }

      // Calculate points for smooth line
      const points = data.map((item, index) => {
        const x = padding.left + index * pointWidth;
        const normalizedValue = item.total / maxValue;
        const y =
          canvas.height - padding.bottom - normalizedValue * chartHeight;
        return { x, y, value: item.total, date: item.date };
      });

      // Draw area under the curve
      ctx.beginPath();
      ctx.moveTo(points[0].x, canvas.height - padding.bottom);

      // Draw smooth curve using quadratic curves
      points.forEach((point, index) => {
        if (index === 0) {
          ctx.lineTo(point.x, point.y);
        } else {
          const prevPoint = points[index - 1];
          const midX = (prevPoint.x + point.x) / 2;
          ctx.quadraticCurveTo(
            prevPoint.x,
            prevPoint.y,
            midX,
            (prevPoint.y + point.y) / 2
          );
          if (index < points.length - 1) {
            const nextPoint = points[index + 1];
            const nextMidX = (point.x + nextPoint.x) / 2;
            ctx.quadraticCurveTo(
              point.x,
              point.y,
              nextMidX,
              (point.y + nextPoint.y) / 2
            );
          } else {
            ctx.lineTo(point.x, point.y);
          }
        }
      });

      ctx.lineTo(points[points.length - 1].x, canvas.height - padding.bottom);
      ctx.closePath();
      ctx.fillStyle = "rgba(59, 130, 246, 0.1)";
      ctx.fill();

      // Draw the main line with solid color
      ctx.beginPath();
      points.forEach((point, index) => {
        if (index === 0) {
          ctx.moveTo(point.x, point.y);
        } else {
          const prevPoint = points[index - 1];
          const midX = (prevPoint.x + point.x) / 2;
          ctx.quadraticCurveTo(
            prevPoint.x,
            prevPoint.y,
            midX,
            (prevPoint.y + point.y) / 2
          );
          if (index < points.length - 1) {
            const nextPoint = points[index + 1];
            const nextMidX = (point.x + nextPoint.x) / 2;
            ctx.quadraticCurveTo(
              point.x,
              point.y,
              nextMidX,
              (point.y + nextPoint.y) / 2
            );
          } else {
            ctx.lineTo(point.x, point.y);
          }
        }
      });
      ctx.strokeStyle = "#3b82f6";
      ctx.lineWidth = 3;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.stroke();

      // Draw points
      points.forEach((point) => {
        // Outer circle (white)
        ctx.beginPath();
        ctx.arc(point.x, point.y, 5, 0, Math.PI * 2);
        ctx.fillStyle = "#ffffff";
        ctx.fill();
        ctx.strokeStyle = "#3b82f6";
        ctx.lineWidth = 2;
        ctx.stroke();

        // Inner circle (blue)
        ctx.beginPath();
        ctx.arc(point.x, point.y, 3, 0, Math.PI * 2);
        ctx.fillStyle = "#3b82f6";
        ctx.fill();
      });

      // Draw X-axis labels
      points.forEach((point, index) => {
        if (
          index % Math.ceil(data.length / 6) === 0 ||
          index === data.length - 1
        ) {
          const date = new Date(point.date);
          const label = date.toLocaleDateString("pt-BR", {
            day: "2-digit",
            month: "short",
          });

          ctx.fillStyle = "#6b7280";
          ctx.font = "11px system-ui, -apple-system, sans-serif";
          ctx.textAlign = "center";
          ctx.fillText(label, point.x, canvas.height - padding.bottom + 20);
        }
      });
    },

    formatTheme(theme) {
      if (!theme) return "Sem tema";
      return theme.charAt(0).toUpperCase() + theme.slice(1);
    },

    getThemeBarWidth(count) {
      if (!this.stats?.postsByTheme) return "0%";

      const maxCount = Math.max(...this.stats.postsByTheme.map((t) => t.count));
      const percentage = maxCount > 0 ? (count / maxCount) * 100 : 0;
      return `${percentage}%`;
    },

    async refreshStats() {
      this.refreshing = true;
      try {
        await Promise.all([
          this.loadAnalyticsData(),
          this.checkServerHealth(),
        ]);
        this.$emit("refresh");
      } catch (error) {
        console.error("Erro ao atualizar:", error);
      } finally {
        this.refreshing = false;
      }
    },

    retryLoad() {
      this.$emit("retry");
    },

    formatNumber(num) {
      if (num >= 1000000) {
        return (num / 1000000).toFixed(1) + "M";
      }
      if (num >= 1000) {
        return (num / 1000).toFixed(1) + "K";
      }
      return num.toString();
    },

    truncateText(text, maxLength) {
      if (!text) return "";
      if (text.length <= maxLength) return text;
      return text.substring(0, maxLength) + "...";
    },
  },
};
</script>

<style scoped>
/* Animations */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.6;
    transform: scale(1.05);
  }
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.overview-tab {
  animation: fadeIn 0.3s ease-out;
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;
}

.loading-spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #e5e7eb;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-bottom: 16px;
}

.loading-state p {
  font-size: 16px;
  color: #6b7280;
  font-weight: 500;
}

/* Overview Content */
.overview-content {
  width: 100%;
}

/* Stats Grid */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 24px;
  margin-bottom: 32px;
}

.stat-card {
  background: white;
  border-radius: 16px;
  padding: 28px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04), 0 1px 3px rgba(0, 0, 0, 0.02);
  border: 1px solid #e5e7eb;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  animation: slideIn 0.4s ease-out;
  position: relative;
  overflow: hidden;
}

.stat-card::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: #3b82f6;
  transform: scaleX(0);
  transform-origin: left;
  transition: transform 0.3s ease;
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.08), 0 4px 8px rgba(0, 0, 0, 0.04);
}

.stat-card:hover::before {
  transform: scaleX(1);
}

.stat-visual {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
}

.stat-icon-wrapper {
  width: 64px;
  height: 64px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.stat-card:hover .stat-icon-wrapper {
  transform: scale(1.05);
}

.stat-svg {
  width: 32px;
  height: 32px;
  color: white;
}

.stat-users {
  background: #3b82f6;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.stat-posts {
  background: #10b981;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.stat-likes {
  background: #ef4444;
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
}

.stat-comments {
  background: #f59e0b;
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
}

.stat-visits {
  background: #8b5cf6;
  box-shadow: 0 4px 12px rgba(139, 92, 246, 0.3);
}

.stat-trend {
  display: flex;
  align-items: center;
}

.trend-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  background: #d1fae5;
  color: #065f46;
}

.trend-icon {
  width: 14px;
  height: 14px;
}

.trend-negative {
  background: #fee2e2;
  color: #991b1b;
}

.trend-negative .trend-icon {
  transform: rotate(180deg);
}

.stat-info {
  margin-bottom: 16px;
}

.stat-value {
  font-size: 36px;
  font-weight: 800;
  color: #111827;
  line-height: 1;
  margin-bottom: 8px;
  letter-spacing: -0.02em;
}

.stat-label {
  font-size: 15px;
  color: #6b7280;
  font-weight: 500;
}

.stat-progress {
  height: 6px;
  background: #f3f4f6;
  border-radius: 3px;
  overflow: hidden;
  position: relative;
}

.progress-bar {
  height: 100%;
  background: #3b82f6;
  border-radius: 3px;
  transition: width 0.8s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Charts Section */
.charts-section {
  display: grid;
  grid-template-columns: 1.5fr 1fr;
  gap: 24px;
  margin-bottom: 32px;
}

.chart-card {
  background: white;
  border-radius: 16px;
  padding: 28px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04), 0 1px 3px rgba(0, 0, 0, 0.02);
  border: 1px solid #e5e7eb;
  transition: all 0.3s ease;
}

.chart-card:hover {
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.08), 0 2px 4px rgba(0, 0, 0, 0.04);
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
}

.chart-title {
  font-size: 18px;
  font-weight: 700;
  color: #111827;
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 4px;
}

.title-icon {
  width: 22px;
  height: 22px;
  color: #3b82f6;
}

.chart-subtitle {
  font-size: 13px;
  color: #6b7280;
  margin-top: 4px;
}

.quick-stats {
  display: flex;
  gap: 16px;
}

.quick-stat {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  padding: 8px 14px;
  background: #f9fafb;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

.quick-label {
  font-size: 11px;
  color: #6b7280;
  text-transform: uppercase;
  font-weight: 600;
  letter-spacing: 0.05em;
}

.quick-value {
  font-size: 20px;
  font-weight: 700;
  color: #111827;
  margin-top: 2px;
}

.chart-body {
  position: relative;
}

.line-chart {
  width: 100%;
  height: 280px;
  border-radius: 8px;
}

/* Themes Grid */
.themes-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 16px;
}

.theme-card {
  background: #f9fafb;
  border-radius: 12px;
  padding: 16px;
  border: 1px solid #e5e7eb;
  transition: all 0.3s ease;
  animation: slideIn 0.4s ease-out;
}

.theme-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
  border-color: #3b82f6;
}

.theme-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.theme-icon {
  font-size: 24px;
}

.theme-name {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  text-transform: capitalize;
}

.theme-count {
  font-size: 28px;
  font-weight: 800;
  color: #111827;
  margin-bottom: 12px;
}

.theme-bar-container {
  height: 6px;
  background: #f3f4f6;
  border-radius: 3px;
  overflow: hidden;
}

.theme-bar-fill {
  height: 100%;
  background: #3b82f6;
  border-radius: 3px;
  transition: width 0.6s ease;
}

/* Activity Grid */
.activity-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 24px;
}

.activity-card {
  background: white;
  border-radius: 16px;
  padding: 28px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04), 0 1px 3px rgba(0, 0, 0, 0.02);
  border: 1px solid #e5e7eb;
}

.activity-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.activity-title {
  font-size: 17px;
  font-weight: 700;
  color: #111827;
  display: flex;
  align-items: center;
  gap: 10px;
}

.activity-badge {
  padding: 4px 12px;
  background: #dbeafe;
  color: #1e40af;
  font-size: 12px;
  font-weight: 600;
  border-radius: 12px;
}

.activity-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.activity-item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px;
  background: #f9fafb;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  transition: all 0.3s ease;
}

.activity-item:hover {
  background: #f3f4f6;
  border-color: #d1d5db;
  transform: translateX(4px);
}

.activity-rank {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #3b82f6;
  color: white;
  font-weight: 700;
  font-size: 14px;
  border-radius: 8px;
  flex-shrink: 0;
}

.user-avatar-wrapper {
  flex-shrink: 0;
}

.user-avatar-img {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #e5e7eb;
}

.user-avatar-default {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #3b82f6;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 18px;
  border: 2px solid #e5e7eb;
}

.activity-content {
  flex: 1;
  min-width: 0;
}

.activity-name {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.activity-meta {
  font-size: 12px;
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 6px;
}

.meta-item {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.meta-item svg {
  width: 14px;
  height: 14px;
}

.meta-divider {
  color: #d1d5db;
}

/* System Status */
.system-status {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.status-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px;
  background: #f9fafb;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
}

.status-online .status-indicator {
  position: relative;
}

.status-indicator {
  position: relative;
  width: 12px;
  height: 12px;
}

.status-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #10b981;
  position: absolute;
}

.status-pulse {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #10b981;
  opacity: 0.4;
  position: absolute;
  animation: pulse 2s infinite;
}

.status-info {
  flex: 1;
}

.status-label {
  font-size: 13px;
  color: #6b7280;
  margin-bottom: 2px;
}

.status-value {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
}

.refresh-btn-full {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 12px 20px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 10px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.refresh-btn-full:hover:not(:disabled) {
  background: #2563eb;
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(59, 130, 246, 0.3);
}

.refresh-btn-full:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.refresh-btn-full svg {
  width: 18px;
  height: 18px;
}

.refresh-btn-full svg.spinning {
  animation: spin 1s linear infinite;
}

/* No Data States */
.no-data {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 20px;
  text-align: center;
  color: #9ca3af;
}

.no-data svg {
  width: 48px;
  height: 48px;
  margin-bottom: 12px;
  opacity: 0.5;
}

.no-data p {
  font-size: 14px;
  color: #6b7280;
}

.no-data-small {
  padding: 32px 20px;
  text-align: center;
}

.no-data-small p {
  font-size: 14px;
  color: #9ca3af;
}

/* Error State */
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;
}

.error-icon {
  width: 64px;
  height: 64px;
  color: #ef4444;
  margin-bottom: 16px;
}

.error-state h3 {
  font-size: 20px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 8px;
}

.error-state p {
  font-size: 15px;
  color: #6b7280;
  margin-bottom: 24px;
}

.retry-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 10px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.retry-btn:hover {
  background: #2563eb;
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(59, 130, 246, 0.3);
}

.retry-btn svg {
  width: 18px;
  height: 18px;
}

/* Responsive Design */
@media (max-width: 1280px) {
  .charts-section {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 1024px) {
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .activity-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 640px) {
  .stats-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .stat-card {
    padding: 20px;
  }

  .stat-icon-wrapper {
    width: 52px;
    height: 52px;
  }

  .stat-svg {
    width: 26px;
    height: 26px;
  }

  .stat-value {
    font-size: 28px;
  }

  .chart-card,
  .activity-card {
    padding: 20px;
  }

  .chart-title {
    font-size: 16px;
  }

  .quick-stats {
    flex-direction: column;
    gap: 8px;
  }

  .quick-stat {
    align-items: flex-start;
  }

  .themes-grid {
    grid-template-columns: 1fr;
  }

  .activity-grid {
    gap: 16px;
  }
}
</style>
